/**
 * FlowCard / FlowLabel / FlowMetric — ported from the OnFlow Design System
 * project (v2/components/core/FlowCard.jsx).
 *
 * The v2 surface. Raised fill + 1px inner sheen + diffuse drop shadow — depth,
 * not a hairline outline. `status` tints the edge and adds a soft glow, which
 * is how on-track / drifting / off-track reads at a glance without reading a
 * word.
 */
import type { CSSProperties, ElementType, ReactNode } from 'react';

import type { TrackState } from './FlowStatus.js';

export type FlowCardStatus = Exclude<TrackState, 'neutral'> | 'action';

const PAD = { none: 0, sm: 14, md: 18, lg: 22 } as const;

const EDGE: Record<FlowCardStatus, string> = {
  'on-track': 'var(--on-track)',
  drifting: 'var(--drifting)',
  'off-track': 'var(--off-track)',
  action: 'var(--ember)',
};

const GLOW: Record<FlowCardStatus, string> = {
  'on-track': 'var(--glow-on-track)',
  drifting: 'var(--glow-drifting)',
  'off-track': 'var(--glow-off-track)',
  action: 'var(--ring-ember)',
};

interface FlowCardProps {
  padding?: keyof typeof PAD;
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  level?: 1 | 2 | 3;
  status?: FlowCardStatus;
  glow?: boolean;
  as?: ElementType;
  children?: ReactNode;
  style?: CSSProperties;
}

export function FlowCard({
  padding = 'md',
  radius = 'md',
  level = 1,
  status,
  glow = false,
  as = 'div',
  children,
  style,
}: FlowCardProps) {
  const Tag = as;
  const edge = status ? EDGE[status] : null;
  const glowShadow = glow && status ? `, ${GLOW[status]}` : '';
  return (
    <Tag
      style={{
        position: 'relative',
        background: level >= 2 ? 'var(--paper-2)' : 'var(--paper-1)',
        border: `1px solid ${edge ? `color-mix(in srgb, ${edge} 40%, transparent)` : 'var(--edge)'}`,
        borderRadius: `var(--r-${radius})`,
        padding: PAD[padding],
        boxShadow: `var(--elev-${level})${glowShadow}`,
        color: 'var(--ink-1)',
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

interface FlowLabelProps {
  tone?: 'dim' | 'ink' | 'action';
  children?: ReactNode;
  style?: CSSProperties;
}

/** Mono uppercase micro-label. Same job as v1's Eyebrow, tighter and dimmer. */
export function FlowLabel({ tone = 'dim', children, style }: FlowLabelProps) {
  const c = tone === 'ink' ? 'var(--ink-2)' : tone === 'action' ? 'var(--ember)' : 'var(--ink-3)';
  return (
    <span
      style={{
        display: 'block',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-label-size)',
        fontWeight: 'var(--text-label-weight)' as CSSProperties['fontWeight'],
        letterSpacing: 'var(--text-label-tracking)',
        textTransform: 'uppercase',
        lineHeight: 1,
        color: c,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

interface FlowMetricProps {
  value?: ReactNode;
  suffix?: string;
  size?: 'hero' | 'md' | 'sm';
  color?: string;
  style?: CSSProperties;
}

/** Big Archivo figure with an optional dimmed suffix. The hero of most cards. */
export function FlowMetric({ value, suffix, size = 'md', color, style }: FlowMetricProps) {
  const key = size === 'hero' ? 'metric-hero' : size === 'sm' ? 'metric-sm' : 'metric';
  return (
    <div
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: `var(--text-${key}-size)`,
        lineHeight: `var(--text-${key}-line)`,
        letterSpacing: `var(--text-${key}-tracking)`,
        fontWeight: `var(--text-${key}-weight)` as CSSProperties['fontWeight'],
        fontVariantNumeric: 'tabular-nums',
        color: color || 'var(--ink-1)',
        ...style,
      }}
    >
      {value}
      {suffix && (
        <span
          style={{
            color: 'var(--ink-3)',
            fontWeight: 600,
            fontSize: '0.42em',
            letterSpacing: 0,
            marginLeft: 6,
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}
